"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import {
  campaignAction,
} from "@/components/store/StorefrontAdBanner";
import type {
  PublicStoreCampaign,
} from "@/types/public-campaign";

type Props = {
  campaign: PublicStoreCampaign | null | undefined;
  companyId: number;
  onProducts?: (ids: number[]) => void;
  onLead?: () => void;
};

export default function StorefrontCampaignModal({
  campaign,
  companyId,
  onProducts,
  onLead,
}: Props) {
  const [open, setOpen] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  const campaignId = campaign?.public_id;
  const revision = campaign?.revision;

  useEffect(() => {
    if (!campaignId || revision === undefined) return;

    const key =
      `fornecedor-campaign-modal:${companyId}:${campaignId}:${revision}`;

    let seen = false;

    try {
      seen = window.sessionStorage.getItem(key) === "1";

      if (!seen) {
        window.sessionStorage.setItem(key, "1");
      }
    } catch {
      // Storage indisponível não impede a exibição.
    }

    if (!seen) {
      previousFocus.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      setOpen(true);
    }
  }, [campaignId, companyId, revision]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    closeButton.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const dialog =
        closeButton.current?.closest('[role="dialog"]');

      if (!dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) return;

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

      previousFocus.current?.focus();
    };
  }, [open]);

  if (!open || !campaign?.desktop_art_url) {
    return null;
  }

  const action = campaignAction(
    campaign,
    onProducts,
    onLead
  );

  const actionLabel =
    campaign.action_label?.trim() ||
    campaign.title?.trim() ||
    "Abrir campanha";

  const artwork = (
    <picture>
      {campaign.mobile_art_url ? (
        <source
          media="(max-width: 639px)"
          srcSet={campaign.mobile_art_url}
        />
      ) : null}

      <img
        src={campaign.desktop_art_url}
        alt={campaign.title?.trim() || "Campanha promocional"}
        className="block h-auto max-h-[82dvh] w-full object-contain"
      />
    </picture>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-950/80 p-3 backdrop-blur-[2px] sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          setOpen(false);
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={
          campaign.title?.trim() ||
          "Campanha promocional"
        }
        className="relative my-auto w-full max-w-5xl overflow-hidden rounded-2xl shadow-2xl"
      >
        <button
          ref={closeButton}
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Fechar publicidade"
          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-slate-950 shadow-md transition hover:scale-105 hover:bg-white"
        >
          <X size={18} />
        </button>

        {action.href ? (
          <a
            href={action.href}
            rel={
              action.external
                ? "noopener noreferrer sponsored"
                : undefined
            }
            aria-label={actionLabel}
            className="block overflow-hidden rounded-2xl outline-none focus-visible:ring-4 focus-visible:ring-blue-500"
          >
            {artwork}
          </a>
        ) : action.onClick ? (
          <button
            type="button"
            onClick={() => {
              action.onClick?.();
              setOpen(false);
            }}
            aria-label={actionLabel}
            className="block w-full overflow-hidden rounded-2xl text-left outline-none focus-visible:ring-4 focus-visible:ring-blue-500"
          >
            {artwork}
          </button>
        ) : (
          <div className="overflow-hidden rounded-2xl">
            {artwork}
          </div>
        )}
      </div>
    </div>
  );
}
